import { createFileRoute } from '@tanstack/react-router';
import { useCTUser } from '@ct/oneid/react';
import { Button, EmptyState, Skeleton } from '@vta/ui';
import { Helmet } from 'react-helmet-async';
import { TeamStoryForm } from '../features/submit/TeamStoryForm.tsx';
import { redirectToHostedSignIn } from '../lib/auth.ts';

/**
 * `/submit` — team-story submission form (SUBMIT-001).
 *
 * Sign-in is required (anti-spam + attribution). CT One ID allows external
 * (non-CT) sign-in, so coaches / students / parents can submit with their own
 * account. Backend enforces the same gate via `requireAuth()` on the
 * `/api/submissions/team-story` route.
 */
export const Route = createFileRoute('/submit')({
  component: SubmitPage,
});

function SubmitPage() {
  const { user, isLoading } = useCTUser();

  return (
    <>
      <Helmet>
        <title>Submit a team story · VEX Teams Atlas</title>
      </Helmet>
      <div className="mx-auto w-full max-w-2xl px-4 py-10 md:py-14">
        <header className="mb-8 flex flex-col gap-2">
          <p className="text-2xs uppercase tracking-[0.32em] text-fg-subtle">Submit</p>
          <h1 className="font-display text-3xl tracking-tight text-fg md:text-4xl">
            Tell us about your team
          </h1>
          <p className="max-w-prose text-sm text-fg-muted">
            Share a bio, robot description, and photos. Submissions are reviewed by an admin
            before appearing on the team page.
          </p>
        </header>

        {isLoading ? (
          <div className="flex flex-col gap-3" role="status" aria-live="polite">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : user ? (
          <TeamStoryForm defaultEmail={user.primaryContactEmail ?? undefined} />
        ) : (
          <EmptyState
            title="Sign in to submit"
            description="To reduce spam, we ask submitters to sign in. Your account is only used to attribute the submission — it is never displayed publicly."
            action={
              <Button
                variant="accent"
                size="md"
                onClick={() => redirectToHostedSignIn(window.location.href)}
              >
                Sign in to continue
              </Button>
            }
          />
        )}
      </div>
    </>
  );
}
